<template>
  <div class="components-demo">
    <header class="demo-header">
      <div class="header-content">
        <h1 class="demo-title">
          <Icon name="settings-gear" :size="24" />
          VSCode Components Demo
        </h1>
        <div class="demo-actions">
          <Button 
            variant="secondary" 
            @click="testCompactNotification"
            title="Test compact VS Code notification with dropdown actions"
          >
            Test Notification with Dropdown
          </Button>
          <Button 
            variant="secondary" 
            @click="testMultipleNotifications"
            title="Test multiple notifications for history"
          >
            Test Multiple Notifications
          </Button>
          <Button 
            variant="secondary" 
            @click="testErrorNotification"
            title="Test error notification"
          >
            Test Error
          </Button>
        </div>
      </div>
    </header>

    <SplitView
      v-model:collapsed="sidebarCollapsed"
      v-model:primarySize="sidebarSize"
      orientation="horizontal"
      :min-primary-size="200"
      :max-primary-size="400"
      class="demo-content"
    >
      <template #primary>
        <div class="demo-sidebar">
          <div class="sidebar-section">
            <h3>Search Demo</h3>
            <SearchBox
              v-model="searchValue"
              placeholder="Search components..."
              :result-count="filteredResults"
              :has-filter="hasActiveFilter"
              @search="handleSearch"
              @clear="clearSearch"
              @filter-click="toggleFilter"
            />
          </div>

          <div class="sidebar-section">
            <h3>Settings Scope</h3>
            <SettingsTargetsWidget
              v-model="selectedScope"
              @action="handleScopeAction"
            />
          </div>

          <div class="sidebar-section">
            <h3>Navigation</h3>
            <nav class="demo-nav">
              <button
                v-for="section in demoSections"
                :key="section.id"
                :class="[
                  'nav-item',
                  { 'nav-item--active': activeSection === section.id }
                ]"
                @click="activeSection = section.id"
              >
                <Icon :name="section.icon" :size="16" />
                {{ section.title }}
              </button>
            </nav>
          </div>
        </div>
      </template>

      <template #secondary>
        <div class="demo-main">
          <div class="main-content">
            <!-- Atoms Demo -->
            <section v-if="activeSection === 'atoms'" class="demo-section">
              <h2>Atom Components</h2>
              <p>Basic building blocks with VSCode styling:</p>
              
              <div class="component-showcase">
                <div class="showcase-item">
                  <h4>Buttons</h4>
                  <div class="component-group">
                    <Button variant="primary">Primary</Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button variant="icon" icon="add" />
                    <Button variant="primary" icon="save" size="small">Save</Button>
                    <Button disabled>Disabled</Button>
                  </div>
                </div>

                <div class="showcase-item">
                  <h4>Inputs</h4>
                  <div class="component-group">
                    <InputBox v-model="demoText" placeholder="Text input..." clearable />
                    <InputBox v-model="demoNumber" type="number" :min="0" :max="100" />
                    <InputBox model-value="Read only" readonly />
                  </div>
                </div>

                <div class="showcase-item">
                  <h4>Controls</h4>
                  <div class="component-group">
                    <SelectBox v-model="demoSelect" :options="selectOptions" />
                    <Toggle v-model="demoToggle" label="Enable feature" />
                    <Toggle :model-value="true" label="Disabled" disabled />
                  </div>
                </div>

                <div class="showcase-item">
                  <h4>Icons</h4>
                  <div class="icon-grid">
                    <Icon v-for="icon in iconList" :key="icon" :name="icon" :size="20" />
                  </div>
                </div>

                <div class="showcase-item">
                  <h4>Badges</h4>
                  <div class="component-group">
                    <Badge text="Default" />
                    <Badge variant="success" text="Success" />
                    <Badge variant="warning" text="Warning" />
                    <Badge variant="error" text="Error" />
                    <Badge variant="info" icon="info" text="Info" />
                  </div>
                </div>

                <div class="showcase-item">
                  <h4>Progress Bars</h4>
                  <div class="component-group">
                    <ProgressBar :value="progressValue" show-label />
                    <ProgressBar :value="75" variant="success" show-label />
                    <ProgressBar indeterminate variant="default" show-label />
                  </div>
                </div>

                <div class="showcase-item">
                  <h4>Tooltips</h4>
                  <div class="component-group">
                    <Tooltip content="This is a helpful tooltip!">
                      <Button>Hover me</Button>
                    </Tooltip>
                    <Tooltip content="Error tooltip with custom styling" placement="right">
                      <Button variant="secondary" icon="error">Error Info</Button>
                    </Tooltip>
                  </div>
                </div>

                <div class="showcase-item">
                  <h4>Dropdown</h4>
                  <div class="component-group">
                    <Dropdown
                      v-model="demoDropdown"
                      :options="dropdownOptions"
                      placeholder="Select an option..."
                      @change="handleDropdownChange"
                    />
                    <Dropdown
                      v-model="demoDropdown2"
                      :options="dropdownOptionsWithIcons"
                      placeholder="With icons..."
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- Molecules Demo -->
            <section v-if="activeSection === 'molecules'" class="demo-section">
              <h2>Molecule Components</h2>
              <p>Composite components that combine atoms:</p>
              
              <div class="component-showcase">
                <div class="showcase-item">
                  <h4>Search Box</h4>
                  <SearchBox
                    v-model="moleculeSearch"
                    placeholder="Advanced search..."
                    :result-count="42"
                    :has-filter="true"
                    filter-tooltip="Show advanced filters"
                  />
                </div>

                <div class="showcase-item">
                  <h4>Settings Targets</h4>
                  <SettingsTargetsWidget
                    v-model="moleculeScope"
                    @action="handleMoleculeAction"
                  />
                </div>

                <div class="showcase-item full-width">
                  <h4>Action Bar</h4>
                  <div class="action-bar-demo">
                    <ActionBar 
                      :actions="demoActions"
                      @action="handleActionBarAction"
                    />
                    <div class="action-info">
                      <p><strong>Last Action:</strong> {{ lastActionTriggered || 'None' }}</p>
                    </div>
                  </div>
                </div>

                <div class="showcase-item full-width">
                  <h4>Split View</h4>
                  <div class="split-demo">
                    <SplitView
                      v-model:collapsed="nestedSplitCollapsed"
                      v-model:primarySize="nestedSplitSize"
                      :min-primary-size="100"
                      :max-primary-size="200"
                    >
                      <template #primary>
                        <div class="split-panel">
                          <h5>Primary Panel</h5>
                          <p>This panel can be resized and collapsed.</p>
                          <Button size="small" @click="nestedSplitCollapsed = !nestedSplitCollapsed">
                            {{ nestedSplitCollapsed ? 'Expand' : 'Collapse' }}
                          </Button>
                        </div>
                      </template>
                      <template #secondary>
                        <div class="split-panel">
                          <h5>Secondary Panel</h5>
                          <p>This panel adjusts automatically.</p>
                          <p>Current primary size: {{ nestedSplitSize }}px</p>
                        </div>
                      </template>
                    </SplitView>
                  </div>
                </div>
              </div>
            </section>

            <!-- Organisms Demo -->
            <section v-if="activeSection === 'organisms'" class="demo-section">
              <h2>Organism Components</h2>
              <p>Complex composite components that provide complete functionality:</p>
              
              <div class="component-showcase">
                <div class="showcase-item full-width">
                  <h4>TreeView</h4>
                  <div class="tree-demo">
                    <TreeView
                      :nodes="treeViewNodes"
                      :selected-id="selectedTreeNode || undefined"
                      :expanded-ids="Array.from(expandedTreeNodes)"
                      @select="onTreeNodeSelect"
                      @expand="onTreeNodeExpand"
                      @collapse="onTreeNodeCollapse"
                    />
                    <div class="tree-info">
                      <p><strong>Selected:</strong> {{ selectedTreeNode || 'None' }}</p>
                      <p><strong>Expanded:</strong> {{ Array.from(expandedTreeNodes).join(', ') }}</p>
                    </div>
                  </div>
                </div>
                
                <div class="showcase-item full-width">
                  <h4>Complete Settings Panel</h4>
                  <div class="settings-demo">
                    <SettingsPanel
                      :settings="mockSettings"
                      :is-dark-theme="theme === 'dark'"
                      @update:setting="handleSettingUpdate"
                      @toggle-theme="toggleTheme"
                      @reset-settings="handleSettingsReset"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- Monaco Editor Demo -->
            <section v-if="activeSection === 'monaco-editor'" class="demo-section">
              <h2>Monaco Editor</h2>
              <p>Advanced code editor with Monaco Editor integration and enhanced features:</p>
              
              <div class="component-showcase">
                <div class="showcase-item full-width">
                  <h4>Editor Settings</h4>
                  <div class="editor-settings">
                    <div class="settings-row">
                      <label>Language:</label>
                      <Dropdown
                        v-model="selectedLanguage"
                        :options="languageOptions"
                        placeholder="Select language"
                        @change="handleLanguageChange"
                      />
                    </div>

                    <div class="settings-row">
                      <label>Theme:</label>
                      <Dropdown
                        v-model="selectedTheme"
                        :options="themeOptions"
                        placeholder="Select theme"
                        @change="handleThemeChange"
                      />
                    </div>

                    <div class="settings-row">
                      <label>Font Size:</label>
                      <InputBox
                        v-model="editorFontSize"
                        type="number"
                        :min="8"
                        :max="24"
                        @input="handleFontSizeChange"
                      />
                    </div>

                    <div class="settings-row">
                      <Toggle
                        v-model="showMinimap"
                        label="Minimap"
                        @change="handleMinimapChange"
                      />
                    </div>

                    <div class="settings-row">
                      <Toggle
                        v-model="editorReadOnly"
                        label="Read Only"
                        @change="handleReadOnlyChange"
                      />
                    </div>

                    <div class="settings-row">
                      <Toggle
                        v-model="wordWrap"
                        label="Word Wrap"
                        @change="handleWordWrapChange"
                      />
                    </div>
                  </div>
                </div>

                <div class="showcase-item full-width">
                  <h4>Action Bar</h4>
                  <ActionBar 
                    :actions="editorActions"
                    class="editor-actions"
                    @action="handleEditorAction"
                  />
                </div>

                <div class="showcase-item full-width">
                  <h4>Monaco Code Editor</h4>
                  <div class="monaco-editor-wrapper" v-if="!editorHasError">
                    <MonacoEditor
                      ref="monacoEditorRef"
                      v-model="editorContent"
                      :language="selectedLanguage"
                      :theme="selectedTheme"
                      :height="editorHeight"
                      :readonly="editorReadOnly"
                      :font-size="editorFontSize"
                      :minimap="showMinimap"
                      :word-wrap="wordWrap ? 'on' : 'off'"
                      @change="handleMonacoEditorChange"
                      @focus="handleMonacoEditorFocus"
                      @blur="handleMonacoEditorBlur"
                      @cursor-position-changed="handleCursorPositionChanged"
                      @ready="handleMonacoEditorReady"
                      @error="handleMonacoEditorError"
                    />
                  </div>
                  <div class="editor-info" v-else>
                    <div class="info-message">
                      <Icon name="error" :size="16" class="error-icon" />
                      <span>Monaco Editor failed to load. This might be due to missing dependencies or configuration issues.</span>
                    </div>
                    <div class="fallback-info">
                      <p>Please check the console for more details about the Monaco Editor initialization error.</p>
                    </div>
                  </div>
                </div>

                <div class="showcase-item full-width">
                  <h4>Editor Status</h4>
                  <div class="editor-status">
                    <div class="status-grid">
                      <div class="status-item">
                        <strong>Ready:</strong>
                        <Badge 
                          :variant="editorReady ? 'success' : 'warning'" 
                          :text="editorReady ? 'Ready' : 'Loading'" 
                        />
                      </div>
                      <div class="status-item">
                        <strong>Language:</strong>
                        <Badge variant="info" :text="selectedLanguage" />
                      </div>
                      <div class="status-item">
                        <strong>Theme:</strong>
                        <Badge variant="default" :text="selectedTheme" />
                      </div>
                      <div class="status-item">
                        <strong>Position:</strong>
                        <Badge 
                          variant="default" 
                          :text="`Line ${currentCursorPosition.lineNumber}, Col ${currentCursorPosition.column}`" 
                        />
                      </div>
                      <div class="status-item">
                        <strong>Characters:</strong>
                        <Badge variant="default" :text="editorContent.length.toString()" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="showcase-item full-width">
                  <h4>Sample Content</h4>
                  <div class="sample-content">
                    <div class="sample-buttons">
                      <Button
                        v-for="sample in sampleContent"
                        :key="sample.name"
                        @click="loadSample(sample)"
                        :variant="sample.language === selectedLanguage ? 'primary' : 'secondary'"
                        size="small"
                      >
                        <Icon :name="sample.icon" :size="14" />
                        {{ sample.name }}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Validation Demo -->
            <section v-if="activeSection === 'validation'" class="demo-section">
              <h2>Form Validation</h2>
              <p>Comprehensive form validation with error handling and user feedback:</p>
              
              <div class="component-showcase">
                <div class="showcase-item full-width">
                  <h4>Basic Field Validation</h4>
                  <div class="validation-demo">
                    <FormGroup
                      title="User Registration"
                      description="Create a new user account with validation"
                      layout="vertical"
                      spacing="normal"
                    >
                      <FormField
                        label="Username"
                        description="Must be unique and at least 3 characters"
                        help-text="Only letters, numbers, and underscores allowed"
                        required
                        :validation-rules="usernameRules"
                        show-validation-icon
                      >
                        <template #default="{ fieldId, hasError, errorMessages, showValidationIcon }">
                          <InputBox
                            :id="fieldId"
                            v-model="validationForm.fields.username.value.value"
                            placeholder="Enter username"
                            :has-error="hasError"
                            :error-messages="errorMessages"
                            :show-validation-icon="showValidationIcon"
                          />
                        </template>
                      </FormField>
                      
                      <FormField
                        label="Email Address"
                        description="We'll send confirmation to this address"
                        required
                        :validation-rules="emailRules"
                        show-validation-icon
                      >
                        <template #default="{ fieldId, hasError, errorMessages, showValidationIcon }">
                          <InputBox
                            :id="fieldId"
                            v-model="validationForm.fields.email.value.value"
                            type="email"
                            placeholder="user@example.com"
                            :has-error="hasError"
                            :error-messages="errorMessages"
                            :show-validation-icon="showValidationIcon"
                          />
                        </template>
                      </FormField>
                      
                      <FormField
                        label="Password"
                        help-text="Must contain uppercase, lowercase, and number"
                        required
                        :validation-rules="passwordRules"
                        show-validation-icon
                      >
                        <template #default="{ fieldId, hasError, errorMessages, showValidationIcon }">
                          <InputBox
                            :id="fieldId"
                            v-model="validationForm.fields.password.value.value"
                            type="password"
                            placeholder="Enter password"
                            :has-error="hasError"
                            :error-messages="errorMessages"
                            :show-validation-icon="showValidationIcon"
                          />
                        </template>
                      </FormField>
                      
                      <FormField
                        label="Age"
                        description="Must be 18 or older"
                        required
                        :validation-rules="ageRules"
                        show-validation-icon
                      >
                        <template #default="{ fieldId, hasError, errorMessages, showValidationIcon }">
                          <InputBox
                            :id="fieldId"
                            v-model="validationForm.fields.age.value.value"
                            type="number"
                            placeholder="Enter age"
                            :min="13"
                            :max="120"
                            :has-error="hasError"
                            :error-messages="errorMessages"
                            :show-validation-icon="showValidationIcon"
                          />
                        </template>
                      </FormField>
                      
                      <FormField
                        label="Country"
                        required
                        :validation-rules="countryRules"
                        show-validation-icon
                      >
                        <template #default="{ fieldId, hasError, errorMessages, showValidationIcon }">
                          <SelectBox
                            :id="fieldId"
                            v-model="validationForm.fields.country.value.value"
                            placeholder="Select country"
                            :options="countryOptions"
                            :has-error="hasError"
                            :error-messages="errorMessages"
                            :show-validation-icon="showValidationIcon"
                          />
                        </template>
                      </FormField>
                      
                      <div class="form-actions">
                        <Button
                          variant="primary"
                          @click="handleValidationSubmit"
                          :disabled="!validationForm.isValid.value"
                        >
                          Create Account
                        </Button>
                        <Button
                          variant="secondary"
                          @click="resetValidationForm"
                        >
                          Reset Form
                        </Button>
                      </div>
                    </FormGroup>
                  </div>
                </div>
                
                <div class="showcase-item full-width">
                  <h4>Form Validation State</h4>
                  <div class="validation-state">
                    <div class="state-grid">
                      <div class="state-item">
                        <strong>Form Valid:</strong> 
                        <Badge 
                          :variant="validationForm.isValid.value ? 'success' : 'error'" 
                          :text="validationForm.isValid.value ? 'Valid' : 'Invalid'" 
                        />
                      </div>
                      <div class="state-item">
                        <strong>Form Dirty:</strong>
                        <Badge 
                          :variant="validationForm.isDirty.value ? 'info' : 'default'" 
                          :text="validationForm.isDirty.value ? 'Modified' : 'Clean'" 
                        />
                      </div>
                      <div class="state-item">
                        <strong>Error Count:</strong>
                        <Badge 
                          variant="warning" 
                          :text="Object.keys(validationForm.errors.value).length.toString()" 
                        />
                      </div>
                    </div>
                    
                    <div class="validation-errors">
                      <h5>Current Errors:</h5>
                      <div v-if="Object.keys(validationForm.errors.value).length === 0" class="no-errors">
                        <Icon name="check" :size="16" class="success-icon" />
                        No validation errors
                      </div>
                      <div v-else class="error-list">
                        <div 
                          v-for="(errors, field) in validationForm.errors.value" 
                          :key="field"
                          class="field-errors"
                        >
                          <strong>{{ field }}:</strong>
                          <ul>
                            <li v-for="error in errors" :key="error">{{ error }}</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Data Visualization Demo -->
            <section v-if="activeSection === 'data-viz'" class="demo-section">
              <h2>Data Visualization</h2>
              <p>Tables for data presentation:</p>
              
              <div class="component-showcase">
                <div class="showcase-item full-width">
                  <h4>Data Table</h4>
                  <div class="table-demo">
                    <DataTable
                      :data="tableData"
                      :columns="tableColumns"
                      searchable
                      sortable
                      :page-size="10"
                      :paginate="true"
                      :show-column-toggle="true"
                      :show-export="true"
                      :selection-mode="'multiple'"
                      @row-select="onRowSelect"
                      @sort="onTableSort"
                      @filter="onTableFilter"
                      @export="onTableExport"
                    />
                  </div>
                </div>
                
                <div class="showcase-item full-width">
                  <h4>Table Data Info</h4>
                  <div class="table-info">
                    <div class="info-grid">
                      <div class="info-item">
                        <strong>Total Rows:</strong> {{ tableData.length }}
                      </div>
                      <div class="info-item">
                        <strong>Selected Rows:</strong> {{ selectedRows.length }}
                      </div>
                      <div class="info-item">
                        <strong>Current Sort:</strong> {{ currentSort || 'None' }}
                      </div>
                    </div>
                    <div class="selected-data" v-if="selectedRows.length > 0">
                      <h5>Selected Items:</h5>
                      <ul>
                        <li v-for="row in selectedRows" :key="row.id">
                          {{ row.name }} ({{ row.department }})
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Interactive Demo -->
            <section v-if="activeSection === 'interactive'" class="demo-section">
              <h2>Interactive Demo</h2>
              <p>See how components work together:</p>
              
              <div class="interactive-demo">
                <div class="demo-controls">
                  <div class="control-group">
                    <label>Theme:</label>
                    <Button @click="toggleTheme">
                      Switch to {{ theme === 'light' ? 'Dark' : 'Light' }}
                    </Button>
                  </div>
                  
                  <div class="control-group">
                    <label>Sidebar:</label>
                    <Button @click="sidebarCollapsed = !sidebarCollapsed">
                      {{ sidebarCollapsed ? 'Expand' : 'Collapse' }} Sidebar
                    </Button>
                  </div>
                </div>

                <div class="state-display">
                  <h4>Current State</h4>
                  <pre>{{ JSON.stringify({
                    theme,
                    selectedScope,
                    searchValue,
                    sidebarCollapsed,
                    sidebarSize,
                    demoText,
                    demoNumber,
                    demoToggle
                  }, null, 2) }}</pre>
                </div>
              </div>
            </section>
          </div>
        </div>
      </template>
    </SplitView>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { Badge, Button, Dropdown, Icon, InputBox, ProgressBar, SelectBox, Toggle, Tooltip, TreeView } from '@/components/atoms';
import { ActionBar, SearchBox, SplitView, SettingsTargetsWidget, FormField, FormGroup } from '@/components/molecules';
import { SettingsPanel, DataTable, MonacoEditor } from '@/components/organisms';
import { mockSettings } from '@/mock/settings';
import { useTheme, useFormValidation, validationRules, createValidationSchema } from '@/composables';
import { useNotifications } from '@/composables/useNotifications';

// Define emits for events
const emit = defineEmits<{
  'editor-error': [error: string]
}>()

// Theme management
const { theme, toggleTheme, initTheme } = useTheme();

// Notification management
const { info, success, warning, error } = useNotifications();

// Layout state
const sidebarCollapsed = ref(false);
const sidebarSize = ref(280);
const activeSection = ref('atoms');

// Search state
const searchValue = ref('');
const hasActiveFilter = ref(false);
const filteredResults = computed(() => {
  if (!searchValue.value) return 0;
  return Math.floor(Math.random() * 50) + 1; // Mock results
});

// Settings state
const selectedScope = ref<'user' | 'workspace'>('user');

// Demo data
const demoText = ref('Hello World');
const demoNumber = ref(42);
const demoToggle = ref(true);
const demoSelect = ref('option1');
const demoDropdown = ref('');
const demoDropdown2 = ref('');
const progressValue = ref(45);

// Molecule demo state
const moleculeSearch = ref('');
const moleculeScope = ref<'user' | 'workspace'>('workspace');
const nestedSplitCollapsed = ref(false);
const nestedSplitSize = ref(120);

const selectOptions = [
  { label: 'Option 1', value: 'option1' },
  { label: 'Option 2', value: 'option2' },
  { label: 'Option 3', value: 'option3' },
];

const dropdownOptions = [
  { label: 'Apple', value: 'apple' },
  { label: 'Banana', value: 'banana' },
  { label: 'Cherry', value: 'cherry' },
  { label: 'Date', value: 'date' },
];

const dropdownOptionsWithIcons = [
  { label: 'File', value: 'file', icon: 'file' },
  { label: 'Folder', value: 'folder', icon: 'folder' },
  { label: 'Settings', value: 'settings', icon: 'settings-gear' },
  { label: 'Search', value: 'search', icon: 'search' },
];

const demoSections = [
  { id: 'atoms', title: 'Atoms', icon: 'symbol-class' },
  { id: 'molecules', title: 'Molecules', icon: 'symbol-interface' },
  { id: 'organisms', title: 'Organisms', icon: 'symbol-namespace' },
  { id: 'monaco-editor', title: 'Monaco Editor', icon: 'bracket' },
  { id: 'validation', title: 'Validation', icon: 'check' },
  { id: 'data-viz', title: 'Data Visualization', icon: 'graph' },
  { id: 'interactive', title: 'Interactive', icon: 'debug-alt' },
];

const iconList = [
  'edit', 'delete', 'add', 'close', 'search', 'settings-gear',
  'check', 'error', 'warning', 'info', 'folder', 'file'
];

// Tree view demo state
const selectedTreeNode = ref<string | null>('workspace');
const expandedTreeNodes = ref(new Set(['root', 'workspace', 'editor']));

const treeViewNodes = [
  {
    id: 'root',
    label: 'VSCode Configuration',
    icon: 'settings-gear',
    children: [
      {
        id: 'workspace',
        label: 'Workspace Settings',
        icon: 'folder',
        children: [
          { id: 'ws-editor', label: 'Editor', icon: 'edit' },
          { id: 'ws-files', label: 'Files', icon: 'file' },
          { id: 'ws-search', label: 'Search', icon: 'search' }
        ]
      },
      {
        id: 'user',
        label: 'User Settings',
        icon: 'person',
        children: [
          { id: 'u-editor', label: 'Editor', icon: 'edit' },
          { id: 'u-theme', label: 'Theme', icon: 'symbol-color' },
          { id: 'u-keybindings', label: 'Keybindings', icon: 'keyboard' }
        ]
      },
      {
        id: 'extensions',
        label: 'Extensions',
        icon: 'extensions',
        children: [
          { id: 'ext-typescript', label: 'TypeScript', icon: 'file-code' },
          { id: 'ext-vue', label: 'Vue.js', icon: 'file-code' },
          { id: 'ext-prettier', label: 'Prettier', icon: 'symbol-color' }
        ]
      }
    ]
  }
];

// Event handlers
const handleSearch = (query: string) => {
  console.log('Search:', query);
};

const clearSearch = () => {
  searchValue.value = '';
};

const toggleFilter = () => {
  hasActiveFilter.value = !hasActiveFilter.value;
};

const handleScopeAction = (actionKey: string, scope: 'user' | 'workspace') => {
  console.log(`${actionKey} action for ${scope} scope`);
};

const handleMoleculeAction = (actionKey: string, scope: 'user' | 'workspace') => {
  console.log(`Molecule ${actionKey} for ${scope}`);
};

const handleDropdownChange = (option: any) => {
  console.log('Dropdown changed:', option);
};

// Action bar demo state
const lastActionTriggered = ref<string | null>(null);

const demoActions = [
  {
    id: 'new',
    label: 'New',
    icon: 'add',
    tooltip: 'Create new item',
    keybinding: 'Ctrl+N',
    action: () => console.log('New action')
  },
  {
    id: 'save',
    label: 'Save',
    icon: 'save',
    tooltip: 'Save changes',
    keybinding: 'Ctrl+S',
    action: () => console.log('Save action')
  },
  {
    id: 'copy',
    label: 'Copy',
    icon: 'copy',
    tooltip: 'Copy selection',
    action: () => console.log('Copy action')
  },
  {
    type: 'separator' as const,
    id: 'sep1'
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings-gear',
    tooltip: 'Open settings',
    action: () => console.log('Settings action')
  }
];

const handleActionBarAction = (actionId: string) => {
  lastActionTriggered.value = actionId;
  console.log('Action bar action triggered:', actionId);
};

// Tree view event handlers
const onTreeNodeSelect = (nodeId: string) => {
  selectedTreeNode.value = nodeId;
  console.log('Selected tree node:', nodeId);
};

const onTreeNodeExpand = (nodeId: string) => {
  expandedTreeNodes.value.add(nodeId);
  console.log('Expanded tree node:', nodeId);
};

const onTreeNodeCollapse = (nodeId: string) => {
  expandedTreeNodes.value.delete(nodeId);
  console.log('Collapsed tree node:', nodeId);
};

// Settings panel event handlers
const handleSettingUpdate = (settingId: string, value: any) => {
  console.log(`Setting ${settingId} updated to:`, value);
  // In a real app, this would update the settings store/backend
};

const handleSettingsReset = () => {
  console.log('Reset all settings to defaults');
  // In a real app, this would reset all modified settings
};

// Validation form setup
const usernameRules = [
  validationRules.required(),
  validationRules.minLength(3),
  validationRules.pattern(/^[a-zA-Z0-9_]+$/, 'Only letters, numbers, and underscores allowed')
];

const emailRules = createValidationSchema.email(true);

const passwordRules = createValidationSchema.password(8, true);

const ageRules = [
  validationRules.required(),
  validationRules.numeric(),
  validationRules.min(18, 'Must be 18 or older'),
  validationRules.max(120, 'Age must be realistic')
];

const countryRules = [
  validationRules.required('Please select a country')
];

const validationForm = useFormValidation({
  username: { initialValue: '', rules: usernameRules },
  email: { initialValue: '', rules: emailRules },
  password: { initialValue: '', rules: passwordRules },
  age: { initialValue: '', rules: ageRules },
  country: { initialValue: '', rules: countryRules }
});

const countryOptions = [
  { label: 'United States', value: 'US' },
  { label: 'Canada', value: 'CA' },
  { label: 'United Kingdom', value: 'GB' },
  { label: 'Germany', value: 'DE' },
  { label: 'France', value: 'FR' },
  { label: 'Japan', value: 'JP' },
  { label: 'Australia', value: 'AU' }
];

// Validation form handlers
const handleValidationSubmit = () => {
  validationForm.markAllTouched();
  const isValid = validationForm.validate();
  
  if (isValid) {
    const formData = validationForm.getFormData();
    console.log('Form submitted successfully:', formData);
    alert('Account created successfully!');
  } else {
    console.log('Form validation failed');
    alert('Please fix validation errors before submitting');
  }
};

const resetValidationForm = () => {
  validationForm.reset();
  console.log('Form reset to initial state');
};

// Data visualization state and data
interface TableRowData {
  id: number;
  name: string;
  email: string;
  department: string;
  role: string;
  status: string;
  lastLogin: string;
  projectCount: number;
}

const selectedRows = ref<TableRowData[]>([]);
const currentSort = ref<string | null>(null);

// Table data
const tableData = [
  {
    id: 1,
    name: 'John Doe',
    email: 'john.doe@example.com',
    department: 'Engineering',
    role: 'Senior Developer',
    status: 'Active',
    lastLogin: '2024-01-15',
    projectCount: 8
  },
  {
    id: 2,
    name: 'Jane Smith',
    email: 'jane.smith@example.com',
    department: 'Design',
    role: 'UX Designer',
    status: 'Active',
    lastLogin: '2024-01-14',
    projectCount: 12
  },
  {
    id: 3,
    name: 'Mike Johnson',
    email: 'mike.johnson@example.com',
    department: 'Engineering',
    role: 'Tech Lead',
    status: 'Away',
    lastLogin: '2024-01-12',
    projectCount: 6
  },
  {
    id: 4,
    name: 'Sarah Wilson',
    email: 'sarah.wilson@example.com',
    department: 'Product',
    role: 'Product Manager',
    status: 'Active',
    lastLogin: '2024-01-15',
    projectCount: 15
  },
  {
    id: 5,
    name: 'David Brown',
    email: 'david.brown@example.com',
    department: 'Engineering',
    role: 'Frontend Developer',
    status: 'Inactive',
    lastLogin: '2024-01-08',
    projectCount: 4
  },
  {
    id: 6,
    name: 'Emily Davis',
    email: 'emily.davis@example.com',
    department: 'Design',
    role: 'Visual Designer',
    status: 'Active',
    lastLogin: '2024-01-15',
    projectCount: 9
  },
  {
    id: 7,
    name: 'Chris Miller',
    email: 'chris.miller@example.com',
    department: 'DevOps',
    role: 'DevOps Engineer',
    status: 'Active',
    lastLogin: '2024-01-14',
    projectCount: 11
  },
  {
    id: 8,
    name: 'Lisa Anderson',
    email: 'lisa.anderson@example.com',
    department: 'QA',
    role: 'QA Engineer',
    status: 'Active',
    lastLogin: '2024-01-15',
    projectCount: 7
  },
  {
    id: 9,
    name: 'Tom Wilson',
    email: 'tom.wilson@example.com',
    department: 'Engineering',
    role: 'Backend Developer',
    status: 'Away',
    lastLogin: '2024-01-13',
    projectCount: 10
  },
  {
    id: 10,
    name: 'Anna Garcia',
    email: 'anna.garcia@example.com',
    department: 'Product',
    role: 'Product Designer',
    status: 'Active',
    lastLogin: '2024-01-15',
    projectCount: 13
  },
  {
    id: 11,
    name: 'Robert Taylor',
    email: 'robert.taylor@example.com',
    department: 'Engineering',
    role: 'Full Stack Developer',
    status: 'Active',
    lastLogin: '2024-01-14',
    projectCount: 9
  },
  {
    id: 12,
    name: 'Jennifer Lee',
    email: 'jennifer.lee@example.com',
    department: 'Marketing',
    role: 'Marketing Manager',
    status: 'Active',
    lastLogin: '2024-01-15',
    projectCount: 5
  }
];

const tableColumns = [
  {
    key: 'name',
    title: 'Name',
    sortable: true,
    filterable: true,
    width: '200px'
  },
  {
    key: 'email',
    title: 'Email',
    sortable: true,
    filterable: true,
    width: '250px'
  },
  {
    key: 'department',
    title: 'Department',
    sortable: true,
    filterable: true,
    width: '150px'
  },
  {
    key: 'role',
    title: 'Role',
    sortable: true,
    filterable: true,
    width: '200px'
  },
  {
    key: 'status',
    title: 'Status',
    sortable: true,
    filterable: true,
    width: '100px',
    render: (value: string) => {
      const variants = {
        'Active': 'success',
        'Away': 'warning',
        'Inactive': 'error'
      };
      return `<span class="status-badge status-${variants[value as keyof typeof variants]}">${value}</span>`;
    }
  },
  {
    key: 'lastLogin',
    title: 'Last Login',
    sortable: true,
    width: '120px'
  },
  {
    key: 'projectCount',
    title: 'Projects',
    sortable: true,
    width: '80px',
    align: 'center' as const
  }
];

// Data visualization event handlers
const onRowSelect = (selectedRowData: TableRowData[]) => {
  selectedRows.value = selectedRowData;
  console.log('Selected rows:', selectedRowData);
};

const onTableSort = (sortInfo: { column: string; direction: 'asc' | 'desc' }) => {
  currentSort.value = `${sortInfo.column} (${sortInfo.direction})`;
  console.log('Table sorted:', sortInfo);
};

const onTableFilter = (filterInfo: { column?: string; value: string }) => {
  console.log('Table filtered:', filterInfo);
};

const onTableExport = (format: 'csv' | 'json' | 'excel') => {
  console.log(`Export table as ${format}`);
  alert(`Table exported as ${format.toUpperCase()}`);
};

// Monaco Editor state
const editorContent = ref('{\n  "name": "sample-project",\n  "version": "1.0.0"\n}');
const editorHeight = ref(500);
const selectedLanguage = ref('json');
const selectedTheme = ref<'vs' | 'vs-dark' | 'hc-black' | 'hc-light'>('vs-dark');
const editorFontSize = ref(14);
const showMinimap = ref(false);
const editorReadOnly = ref(false);
const wordWrap = ref(false);
const editorReady = ref(false);
const editorHasError = ref(false);
const monacoEditorRef = ref();
const currentCursorPosition = ref({ lineNumber: 1, column: 1 });

// Monaco Editor options - Only supported languages
const languageOptions = [
  { value: 'json', label: 'JSON', icon: 'json' },
  { value: 'yaml', label: 'YAML', icon: 'symbol-array' },
  { value: 'xml', label: 'XML', icon: 'symbol-structure' },
  { value: 'plaintext', label: 'Plain Text', icon: 'symbol-text' }
];

const themeOptions = [
  { value: 'vs', label: 'Light', icon: 'symbol-light' },
  { value: 'vs-dark', label: 'Dark', icon: 'symbol-dark' },
  { value: 'hc-black', label: 'High Contrast Dark', icon: 'symbol-dark' },
  { value: 'hc-light', label: 'High Contrast Light', icon: 'symbol-light' }
];

const sampleContent = [
  {
    name: 'package.json',
    language: 'json',
    icon: 'json',
    content: '{\n  "name": "sample-project",\n  "version": "1.0.0",\n  "description": "A demo project",\n  "main": "index.js",\n  "scripts": {\n    "dev": "vite",\n    "build": "vite build"\n  }\n}'
  },
  {
    name: 'config.yaml',
    language: 'yaml',
    icon: 'symbol-array',
    content: 'name: sample-project\nversion: 1.0.0\ndescription: A demo project\nenvironment:\n  development:\n    debug: true\n    port: 3000\n  production:\n    debug: false\n    port: 80'
  },
  {
    name: 'config.xml',
    language: 'xml',
    icon: 'symbol-structure',
    content: '<?xml version="1.0" encoding="UTF-8"?>\n<configuration>\n  <project>\n    <name>sample-project</name>\n    <version>1.0.0</version>\n  </project>\n  <settings>\n    <debug>true</debug>\n    <port>3000</port>\n  </settings>\n</configuration>'
  },
  {
    name: 'readme.txt',
    language: 'plaintext',
    icon: 'symbol-text',
    content: 'Sample Project\n=============\n\nThis is a simple demonstration of Monaco Editor integration.\n\nFeatures:\n- JSON editing with syntax highlighting\n- YAML configuration support\n- XML document editing\n- Plain text editing\n\nInstructions:\n1. Select a language from the dropdown\n2. Edit the content in the editor\n3. Use the action bar for formatting and copying'
  }
];

// Monaco Editor actions
const editorActions = [
  {
    id: 'format',
    label: 'Format Document',
    icon: 'symbol-operator',
    tooltip: 'Format the document',
    keybinding: 'Shift+Alt+F',
    action: () => formatDocument()
  },
  {
    id: 'copy',
    label: 'Copy All',
    icon: 'copy',
    tooltip: 'Copy all content',
    keybinding: 'Ctrl+A Ctrl+C',
    action: () => copyContent()
  },
  {
    id: 'clear',
    label: 'Clear',
    icon: 'clear-all',
    tooltip: 'Clear all content',
    action: () => clearContent()
  },
  {
    type: 'separator' as const,
    id: 'sep1'
  },
  {
    id: 'height',
    type: 'dropdown' as const,
    label: 'Height',
    icon: 'arrow-both',
    tooltip: 'Change editor height',
    items: [
      {
        id: 'height-300',
        label: '300px',
        action: () => setEditorHeight(300)
      },
      {
        id: 'height-500',
        label: '500px',
        action: () => setEditorHeight(500)
      },
      {
        id: 'height-700',
        label: '700px',
        action: () => setEditorHeight(700)
      },
      {
        id: 'height-full',
        label: 'Full Height',
        action: () => setEditorHeight(800)
      }
    ]
  }
];

// Monaco Editor event handlers
const handleLanguageChange = () => {
  console.log('Language changed to:', selectedLanguage.value);
};

const handleThemeChange = () => {
  console.log('Theme changed to:', selectedTheme.value);
};

const handleFontSizeChange = () => {
  console.log('Font size changed to:', editorFontSize.value);
};

const handleMinimapChange = () => {
  console.log('Minimap toggle:', showMinimap.value);
};

const handleReadOnlyChange = () => {
  console.log('Read only toggle:', editorReadOnly.value);
};

const handleWordWrapChange = () => {
  console.log('Word wrap toggle:', wordWrap.value);
};

const handleEditorAction = (actionId: string) => {
  console.log('Editor action triggered:', actionId);
};

// Monaco Editor specific event handlers
const handleMonacoEditorChange = (value: string) => {
  console.log('Monaco Editor content changed:', value.length, 'chars');
};

const handleMonacoEditorFocus = () => {
  console.log('Monaco Editor focused');
};

const handleMonacoEditorBlur = () => {
  console.log('Monaco Editor blurred');
};

const handleCursorPositionChanged = (position: any) => {
  currentCursorPosition.value = position;
  // console.log('Cursor position changed:', position);
};

const handleMonacoEditorReady = (editor: any) => {
  editorReady.value = true;
  console.log('Monaco Editor is ready:', editor);
};

const handleMonacoEditorError = (error: string) => {
  editorReady.value = false
  editorHasError.value = true
  console.error('Monaco Editor error:', error)
  // Emit the error for proper handling
  emit('editor-error', error)
}


const formatDocument = () => {
  console.log('Format document');
  if (monacoEditorRef.value && monacoEditorRef.value.formatDocument) {
    monacoEditorRef.value.formatDocument();
  } else {
    // Fallback for simple JSON formatting
    if (selectedLanguage.value === 'json') {
      try {
        const parsed = JSON.parse(editorContent.value);
        editorContent.value = JSON.stringify(parsed, null, 2);
      } catch (error) {
        console.warn('Invalid JSON, cannot format');
      }
    }
  }
};

const copyContent = async () => {
  try {
    await navigator.clipboard.writeText(editorContent.value);
    console.log('Content copied to clipboard');
  } catch (error) {
    console.error('Failed to copy content:', error);
  }
};

const clearContent = () => {
  editorContent.value = '';
  console.log('Editor content cleared');
};

const setEditorHeight = (height: number) => {
  editorHeight.value = height;
  console.log('Editor height set to:', height);
};

const loadSample = (sample: typeof sampleContent[0]) => {
  selectedLanguage.value = sample.language;
  editorContent.value = sample.content;
  console.log('Loaded sample:', sample.name);
};

// Sync editor theme with website theme
const syncEditorTheme = () => {
  selectedTheme.value = theme.value === 'light' ? 'vs' : 'vs-dark';
};

// Test compact notification layout
const testCompactNotification = () => {
  info({
    text: 'File saved successfully. This is a longer message to test text truncation in the compact layout and it should be truncated with expand option.'
  }, {
    source: '',
    actions: {
      primary: [
        { 
          id: 'undo', 
          label: 'Undo',
          onClick: () => console.log('Undo clicked')
        },
        { 
          id: 'open-file', 
          label: 'Open File',
          onClick: () => console.log('Open file clicked')
        },
        {
          id: 'copy-path',
          label: 'Copy Path',
          icon: 'copy',
          onClick: () => console.log('Copy path clicked')
        }
      ]
    }
  });
};

const testMultipleNotifications = () => {
  // Test different types of notifications for history
  success('Configuration saved successfully!');
  
  setTimeout(() => {
    warning('Some settings require a restart to take effect.');
  }, 500);
  
  setTimeout(() => {
    info({
      text: 'New update available for extension "Configuration Manager"'
    }, {
      actions: {
        primary: [
          { 
            id: 'update', 
            label: 'Update',
            onClick: () => console.log('Update clicked')
          },
          { 
            id: 'later', 
            label: 'Later',
            onClick: () => console.log('Later clicked')
          }
        ]
      }
    });
  }, 1000);
  
  setTimeout(() => {
    info({
      text: 'Backup completed. 1,247 files backed up to cloud storage.'
    }, {
      source: 'Cloud Backup Service',
      sticky: false
    });
  }, 1500);
};

const testErrorNotification = () => {
  error({
    text: 'Failed to connect to database. Connection timeout after 30 seconds.'
  }, {
    source: 'Database Connection',
    actions: {
      primary: [
        { 
          id: 'retry', 
          label: 'Retry',
          onClick: () => console.log('Retry clicked')
        },
        { 
          id: 'settings', 
          label: 'Connection Settings',
          onClick: () => console.log('Settings clicked')
        }
      ]
    },
    sticky: true
  });
};

onMounted(() => {
  initTheme();
  // Initialize editor theme to match website theme
  syncEditorTheme();
});
</script>

<style scoped lang="scss">
.components-demo {
  height: 100vh;
  background-color: var(--vscode-background);
  color: var(--vscode-foreground);
  display: flex;
  flex-direction: column;
}

.demo-header {
  border-bottom: 1px solid var(--vscode-settings-headerBorder);
  padding: var(--spacing-md) var(--spacing-lg);
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.demo-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  font-size: 1.25rem;
  font-weight: 600;
}

.demo-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.demo-content {
  flex: 1;
}

.demo-sidebar {
  padding: var(--spacing-lg);
  background-color: var(--vscode-backgroundSecondary);
  height: 100%;
  overflow-y: auto;
}

.sidebar-section {
  margin-bottom: var(--spacing-xxl);
  
  h3 {
    margin: 0 0 var(--spacing-md) 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-foreground);
  }
}

.demo-nav {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.nav-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-family: var(--vscode-font-family);
  font-size: var(--vscode-font-size);
  color: var(--vscode-foreground);
  opacity: 0.8;
  transition: all 0.1s ease;
  
  &:hover {
    background-color: var(--vscode-list-hoverBackground);
    opacity: 1;
  }
  
  &--active {
    background-color: var(--vscode-list-activeSelectionBackground);
    color: var(--vscode-list-activeSelectionForeground);
    opacity: 1;
  }
}

.demo-main {
  height: 100%;
  overflow-y: auto;
}

.main-content {
  padding: var(--spacing-lg);
}

.demo-section {
  h2 {
    margin: 0 0 var(--spacing-md) 0;
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  p {
    margin: 0 0 var(--spacing-xxl) 0;
    opacity: 0.8;
  }
}

.component-showcase {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-xxl);
}

.showcase-item {
  background-color: var(--vscode-backgroundSecondary);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
  
  &.full-width {
    grid-column: 1 / -1;
  }
  
  h4 {
    margin: 0 0 var(--spacing-md) 0;
    font-size: 1rem;
    font-weight: 600;
  }
}

.component-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(40px, 1fr));
  gap: var(--spacing-md);
  
  // Removed legacy .codicon demo styles after migration to Phosphor
}

.split-demo {
  height: 300px;
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  overflow: hidden;
}

.split-panel {
  padding: var(--spacing-lg);
  height: 100%;
  
  h5 {
    margin: 0 0 var(--spacing-md) 0;
    font-size: 14px;
    font-weight: 600;
  }
  
  p {
    margin: 0 0 var(--spacing-md) 0;
    font-size: 12px;
    opacity: 0.8;
  }
}

.interactive-demo {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxl);
}

.demo-controls {
  display: flex;
  gap: var(--spacing-xxl);
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  
  label {
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-foreground);
  }
}

.state-display {
  background-color: var(--vscode-backgroundSecondary);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
  
  h4 {
    margin: 0 0 var(--spacing-md) 0;
    font-size: 14px;
    font-weight: 600;
  }
  
  pre {
    background-color: var(--vscode-background);
    border: 1px solid var(--vscode-border);
    border-radius: var(--border-radius);
    padding: var(--spacing-md);
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    overflow-x: auto;
    margin: 0;
  }
}

.tree-demo {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--spacing-lg);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  overflow: hidden;
  height: 300px;
  
  .tree-info {
    background-color: var(--vscode-background);
    padding: var(--spacing-md);
    border-left: 1px solid var(--vscode-border);
    min-width: 200px;
    font-size: 12px;
    
    p {
      margin: 0 0 var(--spacing-sm) 0;
    }
    
    strong {
      color: var(--vscode-foreground);
      font-weight: 600;
    }
  }
}

.settings-demo {
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  overflow: hidden;
  height: 600px;
  background-color: var(--vscode-background);
}

// Validation demo styles
.validation-demo {
  background-color: var(--vscode-background);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
  max-height: 600px;
  overflow-y: auto;
}

.form-actions {
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--vscode-border);
}

.validation-state {
  background-color: var(--vscode-background);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
}

.state-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
}

.state-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  strong {
    font-weight: 600;
    color: var(--vscode-foreground);
  }
}

.validation-errors {
  h5 {
    margin: 0 0 var(--spacing-md) 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-foreground);
  }
}

.no-errors {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--vscode-terminal-ansiGreen);
  font-size: 14px;
  
  .success-icon {
    color: var(--vscode-terminal-ansiGreen);
  }
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.field-errors {
  strong {
    font-weight: 600;
    color: var(--vscode-inputValidation-errorForeground);
    margin-bottom: var(--spacing-xs);
    display: block;
  }
  
  ul {
    margin: 0;
    padding-left: var(--spacing-lg);
    color: var(--vscode-inputValidation-errorForeground);
    
    li {
      font-size: 12px;
      margin-bottom: var(--spacing-xs);
    }
  }
}

// Data visualization demo styles
.chart-demo {
  background-color: var(--vscode-background);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
  min-height: 350px;
}

.table-demo {
  background-color: var(--vscode-background);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  overflow: hidden;
  max-height: 600px;
}

.table-info {
  background-color: var(--vscode-background);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius);
  padding: var(--spacing-lg);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
}

.info-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  
  strong {
    font-weight: 600;
    color: var(--vscode-foreground);
  }
}

.selected-data {
  h5 {
    margin: 0 0 var(--spacing-md) 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--vscode-foreground);
  }
  
  ul {
    margin: 0;
    padding-left: var(--spacing-lg);
    
    li {
      font-size: 12px;
      margin-bottom: var(--spacing-xs);
      color: var(--vscode-foreground);
    }
  }
}

// Monaco Editor styles
.editor-settings {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
}

.settings-row {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  
  label {
    font-size: 12px;
    font-weight: 600;
    color: var(--vscode-foreground);
    opacity: 0.9;
  }
}

.editor-container {
  width: 100%;
  border: 1px solid var(--vscode-input-border);
  border-radius: var(--border-radius);
  overflow: hidden;
  background-color: var(--vscode-input-background);
}

.simple-editor {
  width: 100%;
  position: relative;
}

.editor-textarea {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  resize: none;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
  padding: var(--spacing-md);
  box-sizing: border-box;
  
  &::placeholder {
    color: var(--vscode-input-placeholderForeground);
    opacity: 0.6;
  }
  
  &:focus {
    outline: 1px solid var(--vscode-focusBorder);
    outline-offset: -1px;
  }
}

.status-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) var(--spacing-md);
  background-color: var(--vscode-statusBar-background);
  color: var(--vscode-statusBar-foreground);
  font-size: 11px;
  border-top: 1px solid var(--vscode-statusBar-border, var(--vscode-border));
  min-height: 22px;
}

.status-left,
.status-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.status-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: 2px var(--spacing-xs);
  border-radius: 2px;
  transition: background-color 0.1s ease;
  height: 100%;
  
  &:hover {
    background-color: var(--vscode-statusBarItem-hoverBackground);
  }
}

.status-ready {
  color: var(--vscode-terminal-ansiGreen);
}

.sample-content {
  margin-top: var(--spacing-md);
}

.sample-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-sm);
}

.editor-actions {
  margin-bottom: var(--spacing-md);
}

.editor-info {
  margin-bottom: var(--spacing-sm);
  padding: var(--spacing-sm);
  background-color: var(--vscode-inputValidation-warningBackground);
  border: 1px solid var(--vscode-inputValidation-warningBorder);
  border-radius: var(--border-radius);
  
  .info-message {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    font-size: 12px;
    color: var(--vscode-inputValidation-warningForeground);
    
    .warning-icon {
      color: var(--vscode-inputValidation-warningForeground);
    }
  }
}

// Status badge styles (for table demo)
:deep(.status-badge) {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  
  &.status-success {
    background-color: var(--vscode-terminal-ansiGreen);
    color: var(--vscode-background);
  }
  
  &.status-warning {
    background-color: var(--vscode-terminal-ansiYellow);
    color: var(--vscode-background);
  }
  
  &.status-error {
    background-color: var(--vscode-terminal-ansiRed);
    color: var(--vscode-background);
  }
}

@media (max-width: 768px) {
  .component-showcase {
    grid-template-columns: 1fr;
  }
  
  .demo-controls {
    flex-direction: column;
    gap: var(--spacing-lg);
  }
  
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
